import { AdditionalMeta, AlternateHrefLangLink, ContentLanguageMeta, NamedMeta, PaginationAttribute, PropertyMeta } from 'bernie-http';
import { PageData } from 'bernie-http';

import { FlexContext, HrefLang, LanguageCanonical, ViewModelResponse } from 'types/flex-definitions';

export function buildFlexPageData(viewModelResponse: ViewModelResponse, pageId): PageData {
  if (!viewModelResponse || !viewModelResponse.composition || !viewModelResponse.flexContext) {
    return null;
  }

  const additionalMetas: AdditionalMeta[] = [
    buildContentLanguageMeta(viewModelResponse.flexContext),
    ...buildNamedMeta(viewModelResponse),
    ...buildAlternateHrefLangLink(viewModelResponse.hrefLangs),
    ...buildPaginationLinks(viewModelResponse),
    ...buildPropertyMeta(viewModelResponse)
  ];

  const pageData = {
    pageId,
    title: viewModelResponse.composition.title,
    pageName: viewModelResponse.trackingContext.pageName,
    seo: {
      robots: viewModelResponse.composition.meta.robots,
      description: viewModelResponse.composition.meta.description,
      canonical: buildCanonicalUrl(viewModelResponse.languageCanonicals, viewModelResponse.flexContext),
      additionalMeta: additionalMetas
    }
  };
  pageData['hrefNextPage'] = viewModelResponse.hrefNextPage; // should this attribute be moved to bernie?

  return pageData;
}

function buildContentLanguageMeta(context: FlexContext) {
  return new ContentLanguageMeta('Content-language', context.siteContext.locale);
}

function buildNamedMeta(viewModel: ViewModelResponse) {
  const composition = viewModel.composition;

  const keywords = new NamedMeta('keywords', composition.meta.keywords);
  const dataFmSeo = new NamedMeta('data-fm-seo', composition.meta['data-fm-seo']);
  const page = new NamedMeta('ewe:page', 'FLEX');
  const pageType = new NamedMeta('flex:page-type', viewModel.flexContext.searchContext.pageType);
  const templateId = new NamedMeta('flex:template-id', composition.templateName);
  const templateFmId = new NamedMeta('flex:template-fm-id', String(composition.templateId));
  const templateVersion = new NamedMeta('flex:template-version', String(composition.templateVersion));
  const essClientId = new NamedMeta('ESSClientID', viewModel.flexClientId);
  const oipSet = new NamedMeta('oip.set', 'false');
  const oipYes = new NamedMeta('oip.yes', 'false');
  const oipNo = new NamedMeta('oip.no', 'false');
  const oipCanTrack = new NamedMeta('oip.canTrack', 'true');

  return [
    keywords,
    dataFmSeo,
    page,
    pageType,
    templateId,
    templateFmId,
    templateVersion,
    essClientId,
    oipSet,
    oipYes,
    oipNo,
    oipCanTrack
  ].filter(meta => meta.content);
}

function buildPropertyMeta(viewModelResponse: ViewModelResponse) {
  const title = new PropertyMeta('og:title', viewModelResponse.composition.title);
  const type = new PropertyMeta('og:type', 'website');
  const description = new PropertyMeta('og:description', viewModelResponse.composition.meta.description);
  const locale = new PropertyMeta('og:locale', viewModelResponse.flexContext.siteContext.locale);
  const siteName = new PropertyMeta('og:site_name', viewModelResponse.flexContext.siteContext.siteNameForDisplay);
  const url = new PropertyMeta('og:url', buildCanonicalUrl(viewModelResponse.languageCanonicals, viewModelResponse.flexContext));

  return [title, type, description, locale, siteName, url];
}

function buildAlternateHrefLangLink(hrefLangs: HrefLang[]) {
  return hrefLangs
    .filter(hrefLang => hrefLang.locale && hrefLang.url)
    .map(hrefLang => new AlternateHrefLangLink(hrefLang.locale.replace(/_/g, '-'), hrefLang.url));
}

function buildPaginationLinks(viewModel: ViewModelResponse) {
  const nextPage = new PaginationAttribute('next', viewModel.hrefNextPage);
  const previousPage = new PaginationAttribute('prev', viewModel.hrefPreviousPage);

  return [nextPage, previousPage].filter(paginationLink => paginationLink.href);
}

function buildCanonicalUrl(languageCanonicals: LanguageCanonical[], context: FlexContext) {
  const languageCanonical = languageCanonicals.filter(canonical => canonical.locale === context.siteContext.locale)[0];

  return languageCanonical ? languageCanonical.canonicalUrl : '';
}
